import { gql, useLazyQuery } from '@apollo/client';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import {
  GraphRef,
  graphRefToString,
  useGraphRef,
} from 'src/app/graph/hooks/useGraphRef';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  SavedOperationAction,
  SavedOperationLocalState,
  convertRemoteStateToLocalState,
} from './useEditorTabState/useSavedOperationLocalState';
import {
  headersArrayFromString,
  useHeadersManagerContext,
} from './useHeadersManagerContext/useHeadersManagerContext';

export function useSyncSavedOperationsOnLoad({
  savedOperationLocalState,
  dispatchSavedOperationLocalStateAction,
  operationModelsById,
  variableModelsById,
  preflightOperationScriptModelById,
  postflightOperationScriptModelById,
}: {
  savedOperationLocalState: SavedOperationLocalState;
  dispatchSavedOperationLocalStateAction: (
    action: SavedOperationAction,
  ) => void;
  operationModelsById: Record<string, monacoEditor.editor.ITextModel>;
  variableModelsById: Record<string, monacoEditor.editor.ITextModel>;
  preflightOperationScriptModelById: Record<
    string,
    monacoEditor.editor.ITextModel
  >;
  postflightOperationScriptModelById: Record<
    string,
    monacoEditor.editor.ITextModel
  >;
}) {
  const [getUpdatedCollectionEntries] = useLazyQuery<
    GraphQLTypes.GetUpdatedCollectionEntries,
    GraphQLTypes.GetUpdatedCollectionEntriesVariables
  >(gql`
    query GetUpdatedCollectionEntries($collectionEntryIds: [ID!]!) {
      operationCollectionEntries(collectionEntryIds: $collectionEntryIds) {
        collection {
          id
          isShared
          variants {
            id
            graphId
            name
            sharedHeaders
          }
        }
        id
        name
        currentOperationRevision {
          body
          variables
          headers {
            name
            value
          }
          script
          postflightOperationScript
        }
      }
    }
  `);

  const { sharedHeaderDefinitions, setTabbedHeaderDefinitionsForTabId } =
    useHeadersManagerContext();
  const graphRef = useGraphRef();

  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  React.useEffect(() => {
    if (!isInitialLoad) {
      // Already loaded
      return;
    }
    if (
      Object.keys(operationModelsById).length === 0 ||
      Object.keys(variableModelsById).length === 0
    ) {
      // Editors not yet created
      return;
    }

    setIsInitialLoad(false);

    const updateValues = async () => {
      const collectionEntryIds = Object.values(
        savedOperationLocalState,
      ).flatMap((savedEntry) => savedEntry?.collectionEntryId ?? []);

      const updatedCollectionEntries =
        (
          await getUpdatedCollectionEntries({
            variables: { collectionEntryIds },
          })
        ).data?.operationCollectionEntries ?? [];

      Object.entries(savedOperationLocalState).forEach(
        ([tabId, savedEntry]) => {
          const updatedEntry = updatedCollectionEntries.find(
            (updatedCollectionEntry) =>
              updatedCollectionEntry.id === savedEntry?.collectionEntryId,
          );
          if (!savedEntry) {
            return;
          }
          if (!updatedEntry) {
            dispatchSavedOperationLocalStateAction({
              type: 'DeleteOperation',
              collectionEntryId: savedEntry.collectionEntryId,
            });
            return;
          }
          const savedEntryState = convertRemoteStateToLocalState(
            updatedEntry.currentOperationRevision,
          );
          // If we are opening an operation from a collection
          // on a different source variant,
          // inject the source variant's shared headers in the tab.
          const sharedCollectionOriginVariantRef: GraphRef | undefined =
            updatedEntry.collection.isShared &&
            !updatedEntry.collection.variants.some(
              (variant) => variant.id === graphRefToString(graphRef),
            ) &&
            updatedEntry.collection.variants[0]
              ? {
                  graphId: updatedEntry.collection.variants[0].graphId,
                  graphVariant: updatedEntry.collection.variants[0].name,
                }
              : undefined;

          const additionalSharedHeadersToInject =
            sharedCollectionOriginVariantRef
              ? headersArrayFromString(
                  updatedEntry.collection.variants[0]?.sharedHeaders ?? '',
                ).filter(
                  (originSharedHeader) =>
                    !sharedHeaderDefinitions.some(
                      (currentVariantSharedHeader) =>
                        currentVariantSharedHeader.headerName ===
                          originSharedHeader.headerName &&
                        currentVariantSharedHeader.value ===
                          originSharedHeader.value,
                    ),
                )
              : [];
          const oldIsModified =
            savedEntry.operationModified ||
            savedEntry.variablesModified ||
            savedEntry.headersModified ||
            savedEntry.preflightOperationScriptModified ||
            savedEntry.postflightOperationScriptModified;
          dispatchSavedOperationLocalStateAction({
            type: 'UpdateRemoteSavedValue',
            operationTabId: tabId,
            savedEntryState,
          });
          if (!oldIsModified) {
            operationModelsById[tabId]?.setValue(
              savedEntryState.operation ?? '',
            );
            variableModelsById[tabId]?.setValue(
              savedEntryState.variables ?? '',
            );
            setTabbedHeaderDefinitionsForTabId(
              tabId,
              savedEntryState.headers?.concat(
                additionalSharedHeadersToInject,
              ) ?? [],
            );
            preflightOperationScriptModelById[tabId]?.setValue(
              savedEntryState.preflightOperationScript ?? '',
            );
            postflightOperationScriptModelById[tabId]?.setValue(
              savedEntryState.postflightOperationScript ?? '',
            );
          }
        },
      );
    };
    updateValues();
  }, [
    dispatchSavedOperationLocalStateAction,
    getUpdatedCollectionEntries,
    isInitialLoad,
    operationModelsById,
    savedOperationLocalState,
    preflightOperationScriptModelById,
    postflightOperationScriptModelById,
    setTabbedHeaderDefinitionsForTabId,
    variableModelsById,
    graphRef,
    sharedHeaderDefinitions,
  ]);
}
